import { getBackendUrl } from '@/utils/apiUrl';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Search, Grid, List, Camera, Clock, User, Euro, Check, ChevronDown, Trash2, Download } from 'lucide-react';
import { toast } from 'sonner';

const CaseList = ({ user }) => {
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('list'); // 'grid' or 'list'
  const [filters, setFilters] = useState({
    search: '',
    assignedTo: '',
    orderType: '',
    status: '',
    workStep: '',
    branch: '',
    mediatedBy: '',
    contactSearch: '',
    overdueFilter: false
  });
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  // Selection state
  const [selectedCases, setSelectedCases] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  useEffect(() => {
    fetchCases();
  }, []);

  const fetchCases = async () => {
    try {
      const response = await fetch(getBackendUrl() + '/api/cases', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Filter out dummy cases (cases with "Neuer Fall" as customer name and no real data)
        const filteredCases = data.filter(c => {
          // Keep case if customer name is not "Neuer Fall" OR if it has significant data
          return c.customer_name !== 'Neuer Fall' || 
                 (c.customer_email && c.customer_email.length > 0) ||
                 (c.insurance_company && c.insurance_company.length > 0) ||
                 (c.total && c.total > 0);
        });
        setCases(filteredCases);
      }
    } catch (error) {
      console.error('Error fetching cases:', error);
    } finally {
      setLoading(false);
    }
  };

  const deleteCase = async (caseId, caseName, e) => {
    e.stopPropagation(); // Verhindert, dass die Zeile geklickt wird
    
    if (!window.confirm(`Möchten Sie die Akte "${caseName}" ins Archiv verschieben? Sie wird nach 14 Tagen automatisch gelöscht.`)) {
      return;
    }

    try {
      const response = await fetch(`${getBackendUrl()}/api/cases/${caseId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        toast.success('Akte wurde ins Archiv verschoben (14 Tage Aufbewahrung)');
        // Aktualisiere die Liste
        setCases(cases.filter(c => c._id !== caseId));
      } else {
        toast.error('Fehler beim Archivieren der Akte');
      }
    } catch (error) {
      console.error('Error deleting case:', error);
      toast.error('Verbindungsfehler beim Archivieren');
    }
  };

  // Massenlöschen - alle ausgewählten Akten archivieren
  const deleteSelectedCases = async () => {
    if (selectedCases.length === 0) return;
    
    const count = selectedCases.length;
    if (!window.confirm(`Möchten Sie ${count} Akte(n) ins Archiv verschieben? Sie werden nach 14 Tagen automatisch gelöscht.`)) {
      return;
    }

    let successCount = 0;
    let errorCount = 0;

    for (const caseId of selectedCases) {
      try {
        const response = await fetch(`${getBackendUrl()}/api/cases/${caseId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (response.ok) {
          successCount++;
        } else {
          errorCount++;
        }
      } catch (error) {
        console.error('Error deleting case:', caseId, error);
        errorCount++;
      }
    }

    if (successCount > 0) {
      toast.success(`${successCount} Akte(n) ins Archiv verschoben`);
      // Aktualisiere die Liste
      setCases(cases.filter(c => !selectedCases.includes(c._id)));
      setSelectedCases([]);
      setSelectAll(false);
    }
    
    if (errorCount > 0) {
      toast.error(`${errorCount} Akte(n) konnten nicht archiviert werden`);
    }
  };

  const updateWorkStep = async (caseId, newWorkStep, e) => {
    e.stopPropagation();
    
    try {
      const response = await fetch(`${getBackendUrl()}/api/cases/${caseId}/work-step`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ work_step: newWorkStep })
      });

      if (response.ok) {
        toast.success(`Arbeitsschritt aktualisiert: ${newWorkStep}`);
        fetchCases(); // Reload cases
      } else {
        toast.error('Fehler beim Aktualisieren');
      }
    } catch (error) {
      console.error('Error updating work step:', error);
      toast.error('Verbindungsfehler');
    }
  };

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentCases = cases.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(cases.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page
  };

  // Handle select/deselect all
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedCases([]);
      setSelectAll(false);
    } else {
      setSelectedCases(currentCases.map(c => c._id));
      setSelectAll(true);
    }
  };

  // Handle individual case selection
  const handleSelectCase = (caseId) => {
    if (selectedCases.includes(caseId)) {
      setSelectedCases(selectedCases.filter(id => id !== caseId));
      setSelectAll(false);
    } else {
      const newSelected = [...selectedCases, caseId];
      setSelectedCases(newSelected);
      if (newSelected.length === currentCases.length) {
        setSelectAll(true);
      }
    }
  };

  const handleExportData = async () => {
    try {
      if (selectedCases.length === 0) {
        toast.error('❌ Bitte wählen Sie mindestens eine Akte aus');
        return;
      }

      toast.info(`🔄 Exportiere ${selectedCases.length} Akte(n)...`);
      
      const response = await fetch(`${getBackendUrl()}/api/export/selected-cases`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ case_ids: selectedCases })
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `easyclaim24_akten_export_${new Date().toISOString().split('T')[0]}.zip`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success(`✅ ${selectedCases.length} Akte(n) erfolgreich exportiert!`);
        setSelectedCases([]);
        setSelectAll(false);
      } else {
        toast.error('❌ Fehler beim Exportieren der Daten');
      }
    } catch (error) {
      console.error('Export error:', error);
      toast.error('❌ Verbindungsfehler beim Exportieren');
    }
  };

  const getWorkStepBadge = (caseItem) => {
    const workStep = caseItem.work_step || 'Erstellt';
    const bgColor = 'bg-blue-600 hover:bg-blue-700';
    
    return (
      <div className="relative group">
        <button 
          className={`${bgColor} text-white px-4 py-1 rounded text-xs font-medium inline-flex items-center gap-1 transition-colors`}
          data-testid={`work-step-${caseItem._id}`}
        >
          {workStep}
          <ChevronDown className="w-3 h-3" />
        </button>
        <div className="absolute left-0 top-full mt-1 bg-white border border-gray-200 rounded shadow-lg z-10 hidden group-hover:block min-w-[120px]">
          <button
            onClick={(e) => updateWorkStep(caseItem._id, 'Erstellt', e)}
            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
          >
            Erstellt
          </button>
          <button
            onClick={(e) => updateWorkStep(caseItem._id, 'Versendet', e)}
            className="w-full text-left px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 transition-colors"
          >
            Versendet
          </button>
        </div>
      </div>
    );
  };

  // Einfache Fortschrittsanzeige: 4 Schritte visualisiert
  const getStatusProgress = (workflow_status) => {
    // Bestimme aktuellen Schritt (1-4)
    let currentStep = 1; // Neu
    if (workflow_status === 'in-bearbeitung') currentStep = 2;
    else if (workflow_status === 'abgeschlossen') currentStep = 3;
    else if (workflow_status === 'abgerechnet') currentStep = 4;
    
    const steps = [
      { label: '1', title: 'Neu' },
      { label: '2', title: 'Bearbeitung' },
      { label: '3', title: 'Fertig' },
      { label: '4', title: 'Rechnung' }
    ];
    
    return (
      <div className="flex items-center gap-0.5" title={`Schritt ${currentStep} von 4`}>
        {steps.map((step, idx) => {
          const stepNum = idx + 1;
          const isActive = stepNum === currentStep;
          const isCompleted = stepNum < currentStep;
          
          return (
            <div 
              key={idx}
              className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold transition-all
                ${isActive ? 'bg-blue-600 text-white ring-2 ring-blue-200' : ''}
                ${isCompleted ? 'bg-green-500 text-white' : ''}
                ${!isActive && !isCompleted ? 'bg-gray-200 text-gray-500' : ''}
              `}
              title={step.title}
            >
              {isCompleted ? '✓' : step.label}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <Layout user={user}>
      <div className="w-full max-w-full">
        {/* Header with Breadcrumb and Export */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <span className="cursor-pointer hover:text-gray-900">Start</span>
            <span>/</span>
            <span className="font-semibold text-gray-900">Akten</span>
          </div>
          
          {/* Export und Löschen Buttons */}
          <div className="flex items-center gap-3">
            {selectedCases.length > 0 && (
              <span className="text-sm font-medium text-gray-600">
                {selectedCases.length} ausgewählt
              </span>
            )}
            
            {/* Löschen Button */}
            <button
              onClick={deleteSelectedCases}
              disabled={selectedCases.length === 0}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors font-medium shadow-sm ${
                selectedCases.length > 0
                  ? 'bg-red-600 text-white hover:bg-red-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
              data-testid="delete-selected-button"
            >
              <Trash2 className="w-4 h-4" />
              <span className="hidden sm:inline">
                {selectedCases.length > 0 ? `${selectedCases.length} löschen` : 'Löschen'}
              </span>
              <span className="sm:hidden">Löschen</span>
            </button>
            
            {/* Export Button */}
            <button
              onClick={handleExportData}
              disabled={selectedCases.length === 0}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors font-medium shadow-sm ${
                selectedCases.length > 0
                  ? 'bg-green-600 text-white hover:bg-green-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
              data-testid="export-data-button"
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">
                {selectedCases.length > 0 ? `${selectedCases.length} exportieren` : 'Daten exportieren'}
              </span>
              <span className="sm:hidden">Export</span>
            </button>
          </div>
        </div>

        {/* Filter Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          {/* First Row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Suche..."
                value={filters.search}
                onChange={(e) => setFilters({...filters, search: e.target.value})}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                data-testid="search-input"
              />
            </div>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <select
                value={filters.assignedTo}
                onChange={(e) => setFilters({...filters, assignedTo: e.target.value})}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm appearance-none"
              >
                <option value="">Zugewiesen an</option>
                <option value="user1">Benutzer 1</option>
                <option value="user2">Benutzer 2</option>
              </select>
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="tt.mm.jjjj"
                className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
              />
              <input
                type="text"
                placeholder="tt.mm.jjjj"
                className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
              />
            </div>
          </div>

          {/* Second Row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <select
              value={filters.orderType}
              onChange={(e) => setFilters({...filters, orderType: e.target.value})}
              className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
            >
              <option value="">Auftragstyp</option>
              <option value="haftpflicht">Haftpflichtauftrag</option>
              <option value="kasko">Kaskoauftrag</option>
            </select>
            <select
              value={filters.status}
              onChange={(e) => setFilters({...filters, status: e.target.value})}
              className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
            >
              <option value="">Status</option>
              <option value="neu">Neu</option>
              <option value="in-bearbeitung">In Bearbeitung</option>
              <option value="abgeschlossen">Abgeschlossen</option>
            </select>
            <select
              value={filters.workStep}
              onChange={(e) => setFilters({...filters, workStep: e.target.value})}
              className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
            >
              <option value="">Arbeitsschritt</option>
              <option value="erstellt">Erstellt</option>
              <option value="in-arbeit">In Arbeit</option>
              <option value="fertig">Fertiggestellt</option>
            </select>
          </div>

          {/* Third Row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Kontakt/Telefonnummer/Straße"
                value={filters.contactSearch}
                onChange={(e) => setFilters({...filters, contactSearch: e.target.value})}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
              />
            </div>
            <select
              value={filters.branch}
              onChange={(e) => setFilters({...filters, branch: e.target.value})}
              className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
            >
              <option value="">Niederlassung</option>
              <option value="branch1">Niederlassung 1</option>
              <option value="branch2">Niederlassung 2</option>
            </select>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <select
                  value={filters.mediatedBy}
                  onChange={(e) => setFilters({...filters, mediatedBy: e.target.value})}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm appearance-none"
                >
                  <option value="">Vermittelt durch</option>
                  <option value="partner1">Partner 1</option>
                  <option value="partner2">Partner 2</option>
                </select>
              </div>
              <button 
                className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50 transition-colors flex items-center gap-2 text-sm"
                onClick={() => setFilters({...filters, overdueFilter: !filters.overdueFilter})}
              >
                <Search className="w-4 h-4" />
                Fälligkeit überschritten
              </button>
            </div>
          </div>
        </div>

        {/* Sort and View Toggle */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-gray-400 rounded-full"></div>
            <span className="text-sm text-gray-700">Erstellt an</span>
            <ChevronDown className="w-4 h-4 text-gray-500" />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 border rounded transition-colors ${
                viewMode === 'grid' ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
              }`}
              data-testid="grid-view-button"
            >
              <Grid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 border rounded transition-colors ${
                viewMode === 'list' ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
              }`}
              data-testid="list-view-button"
            >
              <List className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Table/List View */}
        {loading ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-4 border-gray-200 border-t-blue-600 mx-auto"></div>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            {viewMode === 'list' ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-center">
                        <input
                          type="checkbox"
                          checked={selectAll}
                          onChange={handleSelectAll}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          data-testid="select-all-checkbox"
                        />
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">FOTO</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">KUNDE / AKTENZEICHEN</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">ARBEITSSCHRITT</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">AMTL. KFZ.</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">HERSTELLER</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">WERTE</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">BEARBEITER</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">STATUS</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">AKTIONEN</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {cases.length === 0 ? (
                      <tr>
                        <td colSpan="10" className="px-4 py-12 text-center text-gray-500">
                          <Camera className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                          <p className="text-sm">Keine Akten vorhanden</p>
                          <p className="text-xs mt-1">Erstellen Sie Ihren ersten Fall über "+ Neuer Fall"</p>
                        </td>
                      </tr>
                    ) : (
                      currentCases.map((caseItem, idx) => (
                        <tr 
                          key={caseItem._id || idx}
                          className="hover:bg-gray-50 transition-colors"
                          data-testid={`case-row-${idx}`}
                        >
                          {/* Checkbox */}
                          <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={selectedCases.includes(caseItem._id)}
                              onChange={() => handleSelectCase(caseItem._id)}
                              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                              data-testid={`case-checkbox-${idx}`}
                            />
                          </td>
                          {/* FOTO */}
                          <td className="px-4 py-4 cursor-pointer" onClick={() => navigate(`/new-case?edit=${caseItem._id}`)}>
                            <div className="w-20 h-20 bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center shadow-sm border border-gray-200">
                              {caseItem.photos && caseItem.photos.length > 0 ? (
                                <img 
                                  src={getBackendUrl() + caseItem.photos[0]}
                                  alt="Fall Vorschau" 
                                  className="w-full h-full object-cover"
                                  data-testid={`case-photo-${caseItem._id}`}
                                />
                              ) : (
                                <Camera className="w-8 h-8 text-gray-400" />
                              )}
                            </div>
                          </td>
                          {/* KUNDE / AKTENZEICHEN */}
                          <td className="px-4 py-4 cursor-pointer" onClick={() => navigate(`/new-case?edit=${caseItem._id}`)}>
                            <div className="flex flex-col">
                              <span className="font-semibold text-gray-900">{caseItem.customer_name || 'Unbekannt'}</span>
                              <span className="text-sm text-gray-600">{caseItem.case_number || caseItem._id?.slice(-8)}</span>
                              <div className="flex items-center gap-1 mt-1">
                                <span className="text-xs text-gray-500">Haftpflichtauftrag</span>
                                <Check className="w-3 h-3 text-green-600" />
                              </div>
                            </div>
                          </td>
                          {/* ARBEITSSCHRITT */}
                          <td className="px-4 py-4 cursor-pointer" onClick={() => navigate(`/new-case?edit=${caseItem._id}`)}>
                            {getWorkStepBadge(caseItem)}
                          </td>
                          {/* AMTL. KFZ. */}
                          <td className="px-4 py-4">
                            <span className="text-sm text-gray-900">{caseItem.license_plate || '-'}</span>
                          </td>
                          {/* HERSTELLER */}
                          <td className="px-4 py-4">
                            <div className="flex flex-col">
                              <span className="text-sm font-medium text-gray-900">{caseItem.manufacturer || '-'}</span>
                              <span className="text-xs text-gray-500">{caseItem.model || ''}</span>
                            </div>
                          </td>
                          {/* WERTE (Rechnungswerte) */}
                          <td className="px-4 py-4">
                            <div className="flex flex-col gap-0.5">
                              <div className="flex items-center gap-1">
                                <span className="text-xs text-gray-500">Netto:</span>
                                <span className="text-sm font-medium text-gray-900">
                                  {caseItem.invoice_total_netto ? parseFloat(caseItem.invoice_total_netto).toFixed(2) : '0.00'} €
                                </span>
                              </div>
                              <div className="flex items-center gap-1">
                                <span className="text-xs text-gray-500">Brutto:</span>
                                <span className="text-sm font-semibold text-blue-700">
                                  {caseItem.invoice_total_brutto ? parseFloat(caseItem.invoice_total_brutto).toFixed(2) : '0.00'} €
                                </span>
                              </div>
                            </div>
                          </td>
                          {/* BEARBEITER */}
                          <td className="px-4 py-4">
                            <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                              <User className="w-4 h-4 text-gray-600" />
                            </div>
                          </td>
                          {/* STATUS */}
                          <td className="px-4 py-4">
                            {getStatusProgress(caseItem.status)}
                          </td>
                          {/* AKTIONEN */}
                          <td className="px-4 py-4">
                            <div className="flex items-center justify-center gap-2">
                              <button
                                onClick={(e) => deleteCase(caseItem._id, caseItem.case_number || caseItem.customer_name, e)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                data-testid={`delete-case-${caseItem._id}`}
                                title="Akte löschen"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            ) : (
              // Grid View
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
                {cases.length === 0 ? (
                  <div className="col-span-full py-12 text-center text-gray-500">
                    <Camera className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p className="text-sm">Keine Akten vorhanden</p>
                  </div>
                ) : (
                  cases.map((caseItem, idx) => (
                    <div 
                      key={caseItem._id || idx}
                      className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                      onClick={() => navigate(`/new-case?edit=${caseItem._id}`)}
                      data-testid={`case-card-${idx}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-20 h-20 bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center flex-shrink-0 shadow-sm border border-gray-200">
                          {caseItem.photos && caseItem.photos.length > 0 ? (
                            <img 
                              src={getBackendUrl() + caseItem.photos[0]}
                              alt="Fall Vorschau" 
                              className="w-full h-full object-cover"
                              data-testid={`case-photo-grid-${caseItem._id}`}
                            />
                          ) : (
                            <Camera className="w-8 h-8 text-gray-400" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-gray-900 truncate">{caseItem.customer_name || 'Unbekannt'}</h3>
                          <p className="text-sm text-gray-600">{caseItem.manufacturer || '-'} {caseItem.model || ''}</p>
                          <p className="text-xs text-gray-500 mt-1">{caseItem.license_plate || '-'}</p>
                          {/* Rechnungswerte */}
                          <div className="mt-2 flex items-center gap-2">
                            <span className="text-sm font-semibold text-blue-700">
                              {caseItem.invoice_total_brutto ? parseFloat(caseItem.invoice_total_brutto).toFixed(2) : '0.00'} € brutto
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="mt-3 pt-3 border-t border-gray-200 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {getWorkStepBadge(caseItem.status)}
                          {getStatusProgress(caseItem.status)}
                        </div>
                        <button
                          onClick={(e) => deleteCase(caseItem._id, caseItem.case_number || caseItem.customer_name, e)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          data-testid={`delete-case-grid-${caseItem._id}`}
                          title="Akte löschen"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
            
            {/* Pagination Controls - Improved Design */}
            {cases.length > 0 && (
              <div className="mt-6 bg-white rounded-lg border border-gray-200 p-4">
                <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                  {/* Info Text - Left */}
                  <div className="text-sm text-gray-600">
                    Zeige <span className="font-semibold text-gray-900">{indexOfFirstItem + 1}-{Math.min(indexOfLastItem, cases.length)}</span> von <span className="font-semibold text-gray-900">{cases.length}</span> Akten
                  </div>

                  {/* Controls - Right */}
                  <div className="flex items-center gap-4">
                    {/* Items per page selector */}
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">Pro Seite:</span>
                      <select
                        value={itemsPerPage}
                        onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                        data-testid="items-per-page-select"
                      >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                    </div>

                    {/* Page navigation */}
                    {totalPages > 1 && (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                          className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                          data-testid="prev-page-button"
                        >
                          ‹ Zurück
                        </button>
                        
                        {/* Page numbers */}
                        <div className="flex items-center gap-1">
                          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                            let pageNum;
                            if (totalPages <= 5) {
                              pageNum = i + 1;
                            } else if (currentPage <= 3) {
                              pageNum = i + 1;
                            } else if (currentPage >= totalPages - 2) {
                              pageNum = totalPages - 4 + i;
                            } else {
                              pageNum = currentPage - 2 + i;
                            }
                            
                            return (
                              <button
                                key={pageNum}
                                onClick={() => handlePageChange(pageNum)}
                                className={`min-w-[40px] h-10 rounded-lg text-sm font-medium transition-all ${
                                  currentPage === pageNum
                                    ? 'bg-blue-600 text-white shadow-md'
                                    : 'border border-gray-300 hover:bg-gray-50 text-gray-700'
                                }`}
                                data-testid={`page-${pageNum}-button`}
                              >
                                {pageNum}
                              </button>
                            );
                          })}
                        </div>
                        
                        <button
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                          className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                          data-testid="next-page-button"
                        >
                          Weiter ›
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default CaseList;
